// src/pages/ProductListingPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { productService } from '../services/productService';
import ProductCard from '../components/Products/ProductCard';

const ProductListingPage = () => {
  const location = useLocation();
  const { addToCart } = useCart();
  const queryParams = new URLSearchParams(location.search);
  const categoryId = queryParams.get('categoryId') || queryParams.get('category'); // Expected category ID
  const isLowPrice = queryParams.get('isLowPrice') === 'true'; // Catch the low price flag
  const categoryName = (queryParams.get('category') || 'Products').replace(/-/g, ' '); // Get category name for display

  // FIXED: Check for 'mode' in query params first, fallback to pathname check
  const modeParam = queryParams.get('mode');
  const isStandard = modeParam ? modeParam.toUpperCase() === 'STANDARD' : location.pathname.includes('standard');
  const orderType = isStandard ? 'STANDARD' : 'QUICK';

  const [sidebarProducts, setSidebarProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [activeTab, setActiveTab] = useState('ALL'); 
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingVariants, setLoadingVariants] = useState(true);

  // 1. Fetch Sidebar Products when Category changes (Only if it's not a lowPrice request)
  useEffect(() => {
    if (!categoryId) return;
    
    if (!isLowPrice) {
      const fetchSidebarData = async () => {
        setLoadingProducts(true);
        try {
          const prods = await productService.getProductsByCategory(categoryId, orderType);
          
          // Deduplicate products based on productId to avoid multiple identical entries in sidebar
          const uniqueProds = [];
          const seen = new Set();
          (prods || []).forEach(p => {
            const id = p.productId || p.id;
            if (id && !seen.has(id)) {
              seen.add(id);
              uniqueProds.push({ ...p, productId: id }); // Normalize to productId
            }
          });
          
          setSidebarProducts(uniqueProds);
        } catch (error) {
          console.error("Failed to load sidebar products", error);
        } finally {
          setLoadingProducts(false);
        }
      };
      fetchSidebarData();
    } else {
      // Hide standard products sidebar loaders for lowPrice variants
      setLoadingProducts(false);
      setSidebarProducts([]);
    }
  }, [categoryId, orderType, isLowPrice]);

  // 2. Fetch Right-side Variants based on Active Tab
  useEffect(() => {
    if (!categoryId) return;
    
    const fetchVariantsData = async () => {
      setLoadingVariants(true);
      try {
        if (activeTab === 'ALL') {
          const res = await productService.getAllVariantsByCategory(categoryId, orderType, 0, 50);
          setVariants(res.content || []);
        } else {
          const res = await productService.getVariantsByProductId(activeTab, orderType);
          setVariants(res || []);
        }
      } catch (error) {
        console.error("Failed to load variants", error);
      } finally {
        setLoadingVariants(false);
      }
    };
    fetchVariantsData();
  }, [categoryId, activeTab, orderType]);

  return (
    <div className="w-full flex flex-col md:flex-row items-start bg-white min-h-screen">
      
      {/* LEFT SIDEBAR - Products List (Hide if low price) */}
      {!isLowPrice && (
        <aside className="w-full md:w-[130px] lg:w-[140px] bg-white border-r border-gray-200 flex-shrink-0 md:sticky md:top-[120px] z-10 md:h-[calc(100vh-120px)] overflow-y-auto hide-scrollbar">
          <style>{`.hide-scrollbar::-webkit-scrollbar { display: none !important; } .hide-scrollbar { -ms-overflow-style: none !important; scrollbar-width: none !important; }`}</style>
          
          <div className="flex md:flex-col overflow-x-auto md:overflow-y-visible">
            <button 
              onClick={() => setActiveTab('ALL')}
              className={`w-[90px] md:w-full flex flex-col items-center justify-center p-3 md:py-5 border-b border-r md:border-r-0 border-gray-100 min-w-[90px] transition-all relative
                 ${activeTab === 'ALL' ? 'text-[#0078ad] bg-sky-50/30' : 'text-gray-500 hover:bg-gray-50'}
              `}
            >
              {activeTab === 'ALL' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0078ad] hidden md:block rounded-r-md"></div>}
              <div className="w-10 h-10 mb-2 flex items-center justify-center rounded-lg">
                 <i className={`fas fa-layer-group text-2xl ${activeTab === 'ALL' ? 'text-[#0078ad]' : 'text-gray-400'}`}></i>
              </div>
              <span className="text-[11px] font-bold text-center leading-tight">All Items</span>
            </button>

            {!loadingProducts && sidebarProducts.map((prod) => {
              const pId = prod.productId || prod.id;
              return (
              <button 
                key={pId}
                onClick={() => setActiveTab(pId)}
                className={`w-[90px] md:w-full flex flex-col items-center justify-center p-3 md:py-4 border-b border-r md:border-r-0 border-gray-100 min-w-[90px] transition-all relative
                   ${activeTab === pId ? 'text-[#0078ad] bg-sky-50/30' : 'text-gray-500 hover:bg-gray-50'}
                `}
              >
                {activeTab === pId && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0078ad] hidden md:block rounded-r-md"></div>}
                <div className="w-10 h-10 mb-2 flex items-center justify-center">
                   <img 
                       src={prod.thumbnailUrl || prod.image || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=100&q=80'} 
                       alt={prod.productName} 
                       className="w-full h-full object-contain mix-blend-multiply"
                   />
                </div>
                <span className="text-[11px] font-bold text-center leading-tight line-clamp-2 px-1">
                  {prod.productName}
                </span>
              </button>
            )})}
          </div>
        </aside>
      )}

      {/* RIGHT MAIN AREA - Variants Grid */}
      <main className="flex-1 w-full bg-white p-4 md:p-6">
        
        <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-100">
          <h1 className="text-xl md:text-2xl font-black text-[#0F2F63] capitalize">
            {categoryName}
          </h1>
          {isLowPrice && (
            <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wider">
              Low Price
            </span>
          )}
        </div>

        {/* Variants Grid Area */}
        {loadingVariants ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="fas fa-spinner fa-spin text-4xl text-[#0078ad] mb-4"></i>
            <p className="text-gray-500 font-bold">Fetching items...</p>
          </div>
        ) : variants.length === 0 ? (
          <div className="text-center py-20 bg-gray-50/50 rounded-xl border border-gray-100 border-dashed">
            <i className="fas fa-box-open text-6xl text-gray-200 mb-4"></i>
            <h3 className="text-xl text-gray-800 font-bold mb-2">No variants found</h3>
            <p className="text-sm text-gray-500">Try selecting a different product from the sidebar.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {variants.map((variant) => (
              <ProductCard 
                key={variant.outletVariantId || variant.variantId || variant.id} 
                product={variant} 
                addToCart={addToCart} 
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProductListingPage;